'use client';

import type { ProductCard, ProductVariant, ShopifyImage } from '@repo/shopify';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useCart } from '@/components/cart/cart-provider';
import { useIdleModule } from '@/lib/use-idle-module';

// Radix dialog modal — heavy and click-only. Kept out of every product card's
// initial listing-page bundle, but loaded during idle time (one shared chunk
// for every card) so the first "+" tap renders it synchronously instead of
// paying the chunk round-trip + Suspense throttle at tap time.
const loadQuickAddModal = () => import('./quick-add-modal').then((m) => m.QuickAddModal);

interface ProductCardQuickAddProps {
	product: ProductCard;
	primaryImage: ShopifyImage | null;
	defaultVariant: ProductVariant | null;
	hasMultipleVariants: boolean;
	hasSizeOption: boolean;
	enableReviews?: boolean;
	rating?: number | null;
	reviewCount?: number | null;
}

export function ProductCardQuickAdd({
	product,
	primaryImage,
	defaultVariant,
	hasMultipleVariants,
	hasSizeOption,
	enableReviews = false,
	rating = null,
	reviewCount = null,
}: ProductCardQuickAddProps) {
	const { addItem } = useCart();
	const [quickAddOpen, setQuickAddOpen] = useState(false);
	const [isAdding, setIsAdding] = useState(false);
	// Mount the modal only after the first open (its closed Radix root is
	// cheap, but 24 cards × a modal each is still work the listing doesn't need).
	const [quickAddMounted, setQuickAddMounted] = useState(false);
	const { module: QuickAddModal, load: loadQuickAdd } = useIdleModule(loadQuickAddModal, {
		enabled: hasMultipleVariants,
	});

	const openQuickAdd = () => {
		loadQuickAdd();
		setQuickAddMounted(true);
		setQuickAddOpen(true);
	};

	if (!product.availableForSale) {
		return null;
	}

	const handleAddToCart = async () => {
		if (hasMultipleVariants) {
			openQuickAdd();
			return;
		}

		if (!defaultVariant?.availableForSale) return;

		setIsAdding(true);
		try {
			await addItem(
				defaultVariant.id,
				1,
				{
					productId: product.id,
					title: product.title,
					handle: product.handle,
					vendor: product.vendor,
					productType: product.productType,
					image: primaryImage,
					variantTitle: defaultVariant.title === 'Default Title' ? undefined : defaultVariant.title,
					sku: defaultVariant.sku,
					price: defaultVariant.price,
				},
				{ analyticsSource: 'product_card_quick_add' },
			);
		} finally {
			setIsAdding(false);
		}
	};

	return (
		<>
			<button
				type="button"
				className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded border border-neutral-300 bg-white transition-colors hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-60 md:hidden"
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					void handleAddToCart();
				}}
				disabled={isAdding || (!hasMultipleVariants && !defaultVariant?.availableForSale)}
				aria-label="Quick add to cart"
			>
				<Plus className="h-4 w-4 text-neutral-700" />
			</button>

			{hasSizeOption && (
				<div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 hidden translate-y-2 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 md:block">
					<div className="mx-1.5 mb-1.5">
						<button
							type="button"
							className="flex w-full items-center justify-center gap-1.5 rounded border border-neutral-300 bg-white py-2.5 text-xs font-bold uppercase tracking-widest text-neutral-900 shadow-sm transition-colors hover:bg-neutral-100"
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();
								openQuickAdd();
							}}
						>
							<Plus className="h-3.5 w-3.5" />
							Choose Size
						</button>
					</div>
				</div>
			)}

			{!hasSizeOption && hasMultipleVariants && (
				<div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 hidden translate-y-2 opacity-0 transition-all duration-200 group-hover:pointer-events-auto group-hover:translate-y-0 group-hover:opacity-100 md:block">
					<div className="m-1.5">
						<button
							type="button"
							className="w-full rounded bg-neutral-900 py-2.5 text-center text-sm font-semibold text-white shadow-lg transition-colors hover:bg-neutral-800"
							onClick={(event) => {
								event.preventDefault();
								event.stopPropagation();
								openQuickAdd();
							}}
						>
							Quick Add
						</button>
					</div>
				</div>
			)}

			{quickAddMounted && QuickAddModal && (
				<QuickAddModal
					open={quickAddOpen}
					onOpenChange={setQuickAddOpen}
					product={product}
					enableReviews={enableReviews}
					rating={rating}
					reviewCount={reviewCount}
				/>
			)}
		</>
	);
}
